import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import Layout from '../components/Layout';
import { Wifi, WifiOff, RefreshCw, PowerOff, Plus, CheckCircle, Trash2 } from 'lucide-react';

// Logout/Disconnect Button matching VPS design (PowerOff icon)
const LogoutButton = ({ onClick, loading, testId }) => (
  <button
    onClick={onClick}
    disabled={loading}
    title="Disconnect"
    style={{
      padding: '8px',
      background: '#fee2e2',
      color: '#dc2626',
      border: 'none',
      borderRadius: '8px',
      cursor: loading ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
    data-testid={testId}
  >
    {loading ? <RefreshCw size={16} className="animate-spin" /> : <PowerOff size={16} />}
  </button>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WhatsAppConnections({ user }) {
  const [sessions, setSessions] = useState([]);
  const [maxSessions, setMaxSessions] = useState(1);
  const [activeCount, setActiveCount] = useState(0);
  const [planName, setPlanName] = useState('Free');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef(null);
  const pollingInterval = useRef(null);

  // Fetch subscription status for plan name
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await axios.get(`${API}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPlanName(response.data.plan_name || 'Free');
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };
    fetchSubscriptionStatus();
  }, []);

  // Fetch all sessions status
  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setSessions(data.sessions || []);
      setMaxSessions(data.max_sessions || 1);
      setActiveCount(data.total_sessions || 0);  // Use total_sessions for slot counting
      setLoading(false);
    } catch (error) {
      console.error('Error checking status:', error);
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Socket connection for real-time updates
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    socketRef.current = io(BACKEND_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      setSocketConnected(true);
      socketRef.current.emit('authenticate', { token });
    });

    socketRef.current.on('disconnect', () => {
      setSocketConnected(false);
    });

    // Listen for WhatsApp events
    socketRef.current.on('qr_code', (data) => {
      console.log('QR code received:', data.session_id);
      fetchStatus();
    });

    socketRef.current.on('whatsapp_connected', (data) => {
      console.log('WhatsApp connected:', data.session_id);
      setActionLoading(null);
      fetchStatus();
    });

    socketRef.current.on('whatsapp_disconnected', (data) => {
      console.log('WhatsApp disconnected:', data.session_id);
      setActionLoading(null);
      fetchStatus();
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [fetchStatus]);

  // Polling as fallback
  useEffect(() => {
    if (!socketConnected) {
      pollingInterval.current = setInterval(fetchStatus, 3000);
    } else {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    }
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [socketConnected, fetchStatus]);

  // Initialize/Connect a session
  const handleConnect = async (sessionId = null) => {
    setActionLoading(sessionId || 'new');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp/initialize`, 
        { session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Initialize response:', response.data);
      setTimeout(() => {
        fetchStatus();
        setActionLoading(null);
      }, 1000);
    } catch (error) {
      console.error('Error connecting:', error);
      alert(error.response?.data?.detail || 'Failed to connect');
      setActionLoading(null);
    }
  };

  // Cancel QR (disconnect without confirmation, no auto-reconnect)
  const handleCancelQR = async (sessionId) => {
    setActionLoading(sessionId);
    try {
      const token = localStorage.getItem('token');
      // Just disconnect - user will manually reconnect for fresh QR
      await axios.post(`${API}/whatsapp/disconnect`, 
        { session_id: sessionId, logout: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchStatus();
    } catch (error) {
      console.error('Error canceling QR:', error);
      alert(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setActionLoading(null);
    }
  };

  // Disconnect a session
  const handleDisconnect = async (sessionId) => {
    if (!window.confirm('Are you sure you want to disconnect this session? You will need to scan QR code again.')) return;
    
    setActionLoading(sessionId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/whatsapp/disconnect`, 
        { session_id: sessionId, logout: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchStatus();
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert(error.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setActionLoading(null);
    }
  };

  // Delete a session (permanent)
  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm(`Are you sure you want to DELETE session ${sessionId}? This action is permanent and cannot be undone.`)) return;
    
    setActionLoading(sessionId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/whatsapp/delete-session`, 
        { session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchStatus();
    } catch (error) {
      console.error('Error deleting session:', error);
      alert(error.response?.data?.detail || 'Failed to delete session');
    } finally {
      setActionLoading(null);
    }
  };

  // Format phone number
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('91') && cleaned.length === 12) {
      return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Get connected sessions count
  const connectedCount = sessions.filter(s => s.status === 'connected').length;
  const totalSessions = sessions.length;
  const canAddMore = totalSessions < maxSessions;

  if (loading) {
    return (
      <Layout user={user}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <RefreshCw className="animate-spin" size={32} style={{ color: '#667eea' }} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div>
        {/* Header */}
        <div style={{ marginBottom: '12px' }}>
          <h1 style={{ fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
            WhatsApp Connections
          </h1>
          <p style={{ color: '#64748b', marginTop: '4px', fontSize: '14px' }}>
            Manage your WhatsApp sessions
          </p>
        </div>

        {/* Stats Bar - Matching user's existing design */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#64748b'
        }}>
          <span>Session Limit: <strong style={{ color: '#1e293b' }}>{maxSessions}</strong></span>
          <span style={{ color: '#e2e8f0' }}>|</span>
          <span>Total: <strong style={{ color: '#1e293b' }}>{totalSessions}</strong></span>
          <span style={{ color: '#e2e8f0' }}>|</span>
          <span>Connected: <strong style={{ color: '#22c55e' }}>{connectedCount}</strong></span>
          <span style={{ color: '#e2e8f0' }}>|</span>
          <span>Plan: <strong style={{ color: '#667eea' }}>{planName}</strong></span>
        </div>

        {/* Session Cards Grid - Responsive wrap */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '16px',
          maxWidth: '100%'
        }}>
          {/* Render existing sessions */}
          {sessions.map((session, index) => (
            <SessionCard
              key={session.session_id || index}
              session={session}
              sessionNumber={index + 1}
              onConnect={() => handleConnect(session.session_id)}
              onDisconnect={() => handleDisconnect(session.session_id)}
              onCancelQR={() => handleCancelQR(session.session_id)}
              onDelete={() => handleDeleteSession(session.session_id)}
              loading={actionLoading === session.session_id}
              formatPhoneNumber={formatPhoneNumber}
            />
          ))}
          
          {/* Add Session Card - Only show if can add more */}
          {canAddMore && (
            <div
              onClick={() => !actionLoading && handleConnect()}
              style={{
                minHeight: '160px',
                border: '2px dashed #22c55e',
                borderRadius: '12px',
                background: '#f0fdf4',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: actionLoading === 'new' ? 'wait' : 'pointer',
                transition: 'all 0.2s',
                opacity: actionLoading === 'new' ? 0.7 : 1
              }}
              data-testid="add-session-card"
            >
              {actionLoading === 'new' ? (
                <>
                  <RefreshCw className="animate-spin" size={32} style={{ color: '#22c55e' }} />
                  <p style={{ color: '#16a34a', marginTop: '12px', fontWeight: '500' }}>
                    Creating Session...
                  </p>
                </>
              ) : (
                <>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px'
                  }}>
                    <Plus size={24} color="white" />
                  </div>
                  <p style={{ color: '#16a34a', fontWeight: '600', margin: 0 }}>
                    Add New Session
                  </p>
                  <p style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>
                    {sessions.length} of {maxSessions} used
                  </p>
                </>
              )}
            </div>
          )}

          {/* Limit Reached Message */}
          {!canAddMore && sessions.length === 0 && (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#64748b'
            }}>
              <p>No sessions available. Please upgrade your plan for more sessions.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

// Session Card Component - Matching user's existing design
function SessionCard({ session, sessionNumber, onConnect, onDisconnect, onCancelQR, onDelete, loading, formatPhoneNumber }) {
  const isConnected = session.status === 'connected';
  const hasQR = session.status === 'qr_ready' && session.qr_code;
  const isInitializing = session.status === 'initializing';
  const isDisconnected = session.status === 'disconnected' || !session.status;

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      border: `2px solid ${isConnected ? '#86efac' : hasQR ? '#fcd34d' : '#e2e8f0'}`,
      overflow: 'hidden'
    }}>
      {/* Card Header - Light green background when connected */}
      <div style={{
        padding: '14px 16px',
        background: isConnected ? '#f0fdf4' : hasQR ? '#fefce8' : '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            background: isConnected ? '#22c55e' : hasQR ? '#f59e0b' : '#94a3b8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isConnected ? <Wifi size={22} color="white" /> : <WifiOff size={22} color="white" />}
          </div>
          <div>
            <p style={{ 
              margin: 0, 
              fontWeight: '600', 
              fontSize: '15px',
              color: isConnected ? '#16a34a' : '#64748b'
            }}>
              {isConnected ? 'Connected' : hasQR ? 'Scan QR' : isInitializing ? 'Initializing...' : 'Disconnected'}
              {isConnected && session.phone_number && (
                <span style={{ fontWeight: '600', marginLeft: '8px', color: '#16a34a', fontSize: '15px' }}>
                  • {formatPhoneNumber(session.phone_number)}
                </span>
              )}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#94a3b8' }}>
              ID: {session.session_id}
            </p>
          </div>
        </div>
        
        {/* Logout Button */}
        {isConnected && (
          <LogoutButton 
            onClick={onDisconnect} 
            loading={loading} 
            testId={`disconnect-btn-${sessionNumber}`}
          />
        )}
      </div>

      {/* Card Body */}
      <div style={{ padding: '16px' }}>
        {/* Connected State - Simple checkmark like VPS */}
        {isConnected && (
          <div style={{
            textAlign: 'center',
            padding: '16px',
            background: '#f0fdf4',
            borderRadius: '10px'
          }}>
            <span style={{ fontSize: '32px' }}>✅</span>
            <p style={{ color: '#166534', margin: '8px 0 0 0', fontWeight: '500', fontSize: '14px' }}>
              WhatsApp Active
            </p>
          </div>
        )}

        {/* QR Code Display - Centered */}
        {hasQR && session.qr_code && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              display: 'inline-block',
              padding: '10px',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              {session.qr_code.startsWith('data:') ? (
                <img src={session.qr_code} alt="QR Code" style={{ width: '180px', height: '180px' }} />
              ) : (
                <img src={`data:image/png;base64,${session.qr_code}`} alt="QR Code" style={{ width: '180px', height: '180px' }} />
              )}
            </div>
            <p style={{ color: '#92400e', marginTop: '12px', fontSize: '12px' }}>
              Open WhatsApp → Linked Devices → Scan
            </p>
            {/* Cancel QR Button */}
            <button
              onClick={onCancelQR}
              disabled={loading}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                background: '#fee2e2',
                color: '#dc2626',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
              data-testid={`cancel-qr-btn-${sessionNumber}`}
            >
              {loading ? 'Canceling...' : 'Cancel QR'}
            </button>
          </div>
        )}

        {/* Initializing State */}
        {isInitializing && (
          <div style={{ padding: '16px 0' }}>
            <RefreshCw className="animate-spin" size={24} style={{ color: '#667eea' }} />
            <p style={{ color: '#64748b', marginTop: '8px', fontSize: '12px' }}>
              Generating QR Code...
            </p>
          </div>
        )}

        {/* Disconnected State - Centered like VPS */}
        {isDisconnected && !loading && (
          <div style={{ textAlign: 'center', padding: '20px 16px' }}>
            {session.phone_number && (
              <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 16px', fontWeight: '400' }}>
                Last: {formatPhoneNumber(session.phone_number)}
              </p>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={onConnect}
                disabled={loading}
                style={{
                  padding: '12px 22px',
                  background: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: loading ? 0.6 : 1
                }}
                data-testid={`connect-btn-${sessionNumber}`}
              >
                <Wifi size={18} />
                Reconnect
              </button>
              <button
                onClick={onDelete}
                disabled={loading}
                style={{
                  padding: '12px 22px',
                  background: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: loading ? 0.6 : 1
                }}
                data-testid={`delete-btn-${sessionNumber}`}
                title="Delete this session permanently"
              >
                <Trash2 size={18} />
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
